import type { CursorData, RemoteSelection } from '@aline/common';
import { CursorEditor, isNotNull } from '@aline/common';
import type { CursorState, RemoteCursorChangeEventListener } from '@slate-yjs/core';
import { getCursorRange } from '@slate-yjs/react';
import type { BaseEditor } from 'slate';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';

import { createSafeEditorVariantHook, useEditor, useTypedEditor } from './editor-hooks';

type Store<T> = readonly [(onStoreChange: () => void) => () => void, () => T];
type CursorStore = Store<Record<string, CursorState<CursorData>>>;

const EDITOR_TO_CURSOR_STORE: WeakMap<BaseEditor, CursorStore> = new WeakMap();

function createRemoteCursorStateStore(editor: CursorEditor): CursorStore {
  let cursors: Record<string, CursorState<CursorData>> = {};

  const changed = new Set<number>();
  const addChanged = changed.add.bind(changed);
  const onStoreChangeListeners: Set<() => void> = new Set();

  let changeHandler: RemoteCursorChangeEventListener | null = null;

  const subscribe = (onStoreChange: () => void) => {
    onStoreChangeListeners.add(onStoreChange);
    if (!changeHandler) {
      changeHandler = event => {
        event.added.forEach(addChanged);
        event.removed.forEach(addChanged);
        event.updated.forEach(addChanged);
        onStoreChangeListeners.forEach(listener => listener());
      };
      CursorEditor.on(editor, 'change', changeHandler);
    }

    return () => {
      onStoreChangeListeners.delete(onStoreChange);
      if (changeHandler && onStoreChangeListeners.size === 0) {
        CursorEditor.off(editor, 'change', changeHandler);
        changeHandler = null;
      }
    };
  };

  const getSnapshot = () => {
    if (changed.size === 0) return cursors;

    changed.forEach(clientId => {
      const state = CursorEditor.cursorState(editor, clientId);
      if (state === null) {
        delete cursors[clientId.toString()];
        return;
      }

      cursors[clientId] = state;
    });

    changed.clear();
    cursors = { ...cursors };
    return cursors;
  };

  return [subscribe, getSnapshot];
}

function getCursorStateStore(editor: CursorEditor): CursorStore {
  const existing = EDITOR_TO_CURSOR_STORE.get(editor);
  if (existing) return existing;

  const store = createRemoteCursorStateStore(editor);
  EDITOR_TO_CURSOR_STORE.set(editor, store);
  return store;
}

function useRemoteCursorStateStore() {
  const editor = useEditor();
  if (!CursorEditor.isCursorEditor(editor))
    throw new Error('useRemoteCursorStateStore must be used within the context of a CursorEditor');

  return getCursorStateStore(editor);
}

export function useRemoteCursorStatesSelector<TSelection = unknown>(
  selector: (cursors: Record<string, CursorState<CursorData>>) => TSelection,
  isEqual?: (a: TSelection, b: TSelection) => boolean
): TSelection {
  const [subscribe, getSnapshot] = useRemoteCursorStateStore();
  return useSyncExternalStoreWithSelector(subscribe, getSnapshot, null, selector, isEqual);
}

export function useRemoteSelections() {
  const editor = useTypedEditor(CursorEditor.isCursorEditor);
  const states = useRemoteCursorStatesSelector(cursorStates => {
    const entries = Object.entries(cursorStates)
      .map(([clientId, state]) => {
        const range = getCursorRange(editor, state);
        if (!range) return null;

        const selection: RemoteSelection = {
          ...state.data,
          ...range,
        };
        const entry: [string, RemoteSelection] = [clientId, selection];
        return entry;
      })
      .filter(isNotNull);
    if (!entries.length) return null;

    return Object.fromEntries(entries);
  });

  return states;
}

export const useSafeRemoteSelections = createSafeEditorVariantHook(
  CursorEditor.isCursorEditor,
  useRemoteSelections,
  null
);
